import React, { useEffect, useState } from "react";
import { FaEnvelope, FaGlobe } from "react-icons/fa";
import CustomInput from "../common/CustomInput";
import ImageUploadModal from "./ImageUploadModal";
import ProfileCard from "./ProfileCard";
import { tp_small } from "../../constants/icons";
import PhoneNumberField from "./common/PhoneNumberField";
import MobileNumberUpdateModal from "../common/MobileNumberUpdateModal";

const AgencyAccountInfo = ({
  isEditing = false,
  isLoading = false,
  isSaving = false,
  // T056: `error` was accepted but never rendered, so a rejected save was
  // invisible. It is now shown above the form, together with the
  // per-field messages in `fieldErrors`.
  error = "",
  fieldErrors = {},
  agencyData = null,
  adminData = null,
  // Dual-role viewers (agent + agency admin) edit their personal details on
  // the AGENT profile; the admin card is replaced by a pointer there.
  isDualRole = false,
  onGoToAgentProfile,
  onAgencyChange,
  onAdminChange,
  onAgencyDocumentChange,
  onAdminDocumentChange,
  onSave,
}) => {
  const [showAdminImageUpload, setShowAdminImageUpload] = useState(false);
  const [showAgencyLogoUpload, setShowAgencyLogoUpload] = useState(false);
  const [agencyLogo, setAgencyLogo] = useState(
    agencyData?.profile_picture_url || null
  );
  const [adminProfileImage, setAdminProfileImage] = useState(
    adminData?.profile_picture_url || null
  );
  const [showMobileModal, setShowMobileModal] = useState(false);

  useEffect(() => {
    setAgencyLogo(agencyData?.profile_picture_url || null);
    setAdminProfileImage(adminData?.profile_picture_url || null);

    console.log("agencyData ", agencyData);
    console.log("adminData", adminData);
  }, [agencyData, adminData]);

  // Helper function to get initials
  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const getAdminName = () => {
    if (!adminData) return "";
    if (adminData.name) return adminData.name;
    const segments = [adminData.first_name, adminData.last_name].filter(Boolean);
    return segments.join(" ").trim();
  };

  const getAdminInitials = () => {
    return getInitials(getAdminName());
  };

  const handleAgencyLogoUpload = () => {
    setShowAgencyLogoUpload(true);
  };

  const handleAdminImageUpload = () => {
    setShowAdminImageUpload(true);
  };

  const handleAdminImageUploadComplete = (croppedImage) => {
    setAdminProfileImage(croppedImage);
    setShowAdminImageUpload(false);
    // Pass file up for admin document update
    if (
      typeof croppedImage === "string" &&
      (croppedImage.startsWith("blob:") || croppedImage.startsWith("data:"))
    ) {
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "admin_document.png", {
            type: "image/png",
          });
          onAdminDocumentChange && onAdminDocumentChange(file);
        });
    }
  };

  const handleAgencyLogoUploadComplete = (croppedImage) => {
    setAgencyLogo(croppedImage);
    setShowAgencyLogoUpload(false);
    // Pass file up for agency document update
    if (
      typeof croppedImage === "string" &&
      (croppedImage.startsWith("blob:") || croppedImage.startsWith("data:"))
    ) {
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "agency_document.png", {
            type: "image/png",
          });
          onAgencyDocumentChange && onAgencyDocumentChange(file);
        });
    }
  };

  return (
    <>
      <div className="sm:px-14 px-4 py-6 space-y-8">
        {/* T056 — a rejected save is visible instead of silently exiting
            edit mode. */}
        {error && (
          <div
            role="alert"
            className="bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] text-sm font-medium rounded-md px-4 py-3"
          >
            {error}
          </div>
        )}

        {/* Agency Details Section - FIRST CARD */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">
              AGENCY DETAILS
            </h2>
          </div>

          <div className="flex items-center gap-6 mb-6">
            {/* Agency Logo */}
            <div className="flex-shrink-0">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center relative  ${!agencyLogo ? 'bg-blue-600' : 'bg-gray-300'}`}>
                {agencyLogo ? (
                  <img
                    src={agencyLogo}
                    alt="Agency Logo"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span className="text-white text-3xl font-bold">
                    {getInitials(agencyData?.name)}
                  </span>
                )}
                <div className="absolute z-10 bottom-0 right-0 w-7 h-7 rounded-sm flex items-center justify-center">
                  <img
                    src={tp_small}
                    alt="tp_small"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Agency Logo Actions */}
            {isEditing && (
              <div className="flex gap-6 justify-center items-center">
                <button
                  onClick={handleAgencyLogoUpload}
                  className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:text-[#2283FF] text-sm font-medium"
                  disabled={!isEditing}
                >
                  Replace
                </button>
              </div>
            )}
          </div>

          {/* Agency Details Form */}
          {/* Row 1: Agency Name */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <CustomInput
              label="Agency name"
              required
              error={fieldErrors.name}
              value={agencyData?.name || ""}
              onChange={(e) =>
                onAgencyChange && onAgencyChange("name", e.target.value)
              }
              placeholder="Enter agency name"
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>

          {/* Row 2: Email Address */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3   gap-4 mb-4">
            <CustomInput
              label="Email address"
              type="email"
              value={adminData?.email || ""}
              placeholder="Enter email address"
              icon={<FaEnvelope className="text-gray-400" />}
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={true}
            />
          </div>

          {/* Row 3: Phone Number */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <PhoneNumberField
              label="Phone number"
              value={adminData?.mobile || ""}
              placeholder="20 1234 5678"
              country="gb"
              disabled
              showEdit={isEditing}
              onEdit={() => setShowMobileModal(true)}
            />
          </div>

          {/* Row 4: Agency Website */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3   gap-4">
            <CustomInput
              label="Agency Website (Optional)"
              value={agencyData?.website || ""}
              onChange={(e) =>
                onAgencyChange && onAgencyChange("website", e.target.value)
              }
              placeholder="https://"
              icon={<FaGlobe className="text-gray-400" />}
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Admin Details Section - SECOND CARD.
            Dual-role users (agent + agency admin) do NOT get the editable
            admin card here: the agent profile owns the personal details, and
            a second editor would let the two diverge (Marin 2026-08-12). The
            pointer card reuses the existing role-switch mechanic. Mobile:
            a full-width card, text wraps normally, nothing is clipped. */}
        {isDualRole ? (
          <div className="bg-white p-6 border border-[#E6E6E6]">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-[11px] font-bold text-[#3C3C3C]">
                ADMIN DETAILS
              </h2>
            </div>
            <p className="text-sm text-[#1A212FB2] leading-relaxed mb-4">
              Your personal details live on your agent profile — switch to
              your agent view to edit them.
            </p>
            <button
              onClick={onGoToAgentProfile}
              className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:bg-[#2283FF1A] text-sm font-medium"
            >
              Switch To Agent
            </button>
          </div>
        ) : (
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">
              ADMIN DETAILS
            </h2>
          </div>

          <div className="flex items-center gap-6 mb-6">
            {/* Admin Profile Picture */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center relative ">
                {adminProfileImage ? (
                  <img
                    src={adminProfileImage}
                    alt="Admin Profile"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span className="text-white text-3xl font-bold">
                    {getAdminInitials()}
                  </span>
                )}
                <div className="absolute z-10 bottom-0 right-0 w-7 h-7 rounded-sm flex items-center justify-center">
                  <img
                    src={tp_small}
                    alt="tp_small"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Profile Picture Actions */}
            {isEditing && (
              <div className="flex gap-6 justify-center items-center">
                <button
                  onClick={handleAdminImageUpload}
                  className="text-[#2283FF] px-4 py-2 rounded-lg bg-[#2283FF0D] hover:text-[#2283FF] text-sm font-medium"
                  disabled={!isEditing}
                >
                  Replace
                </button>
              </div>
            )}
          </div>

          {/* Admin Details Form */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3   gap-4 mb-4">
            <div className="col-span-1">
              <CustomInput
                label="Name"
                value={getAdminName()}
                onChange={(e) =>
                  onAdminChange && onAdminChange("name", e.target.value)
                }
                placeholder="Enter name"
                className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
                disabled={!isEditing}
              />
            </div>
          </div>

          {/* Row 2: License Number */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3   gap-4">
            <CustomInput
              label="License number"
              value={adminData?.license_number || ""}
              onChange={(e) =>
                onAdminChange && onAdminChange("license_number", e.target.value)
              }
              placeholder="FIFA-AG-12345"
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>
        </div>
        )}

        {/* Profile Card Section - Using the reusable component */}
        <ProfileCard disabled={isEditing} />
      </div>

      {/* Admin Profile Image Upload Modal */}
      {showAdminImageUpload && (
        <ImageUploadModal
          onClose={() => setShowAdminImageUpload(false)}
          onUpload={handleAdminImageUploadComplete}
        />
      )}

      {/* Agency Logo Upload Modal */}
      {showAgencyLogoUpload && (
        <ImageUploadModal
          onClose={() => setShowAgencyLogoUpload(false)}
          onUpload={handleAgencyLogoUploadComplete}
        />
      )}

      <MobileNumberUpdateModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        initialMobile={adminData?.mobile || ""}
        onSuccess={(newMobile) => {
          onAdminChange && onAdminChange("mobile", newMobile);
        }}
      />
    </>
  );
};

export default AgencyAccountInfo;
